import { z } from 'zod';
import { PhoneSchema } from './auth';
import { CountryCodeSchema, type CountryCode } from './geo';

/* ------------------------------------------------------------------ */
/* Restaurant onboarding + KYC (W4)                                    */
/*                                                                     */
/* A restaurant is the business (legal entity, tax ids, bank, owner).  */
/* It has one or more outlets (kitchens). Each outlet is onboarded and */
/* goes live on its own, so the Ops review queue is per outlet.        */
/* Documents belong to the restaurant (PAN, cheque, owner ID …) or to  */
/* one outlet (food licence, kitchen photos …).                        */
/* ------------------------------------------------------------------ */

export const BUSINESS_TYPES = [
  'restaurant',
  'cafe',
  'bakery',
  'cloud_kitchen',
  'home_kitchen',
  'sweet_shop',
] as const;
export const BusinessTypeSchema = z.enum(BUSINESS_TYPES);
export type BusinessType = z.infer<typeof BusinessTypeSchema>;

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  restaurant: 'Restaurant',
  cafe: 'Café',
  bakery: 'Bakery',
  cloud_kitchen: 'Cloud kitchen',
  home_kitchen: 'Home kitchen',
  sweet_shop: 'Sweet shop',
};

export const FOOD_TYPES = ['veg', 'non_veg', 'both'] as const;
export const FoodTypeSchema = z.enum(FOOD_TYPES);
export type FoodType = z.infer<typeof FoodTypeSchema>;
export const FOOD_TYPE_LABELS: Record<FoodType, string> = {
  veg: 'Pure veg',
  non_veg: 'Non-veg only',
  both: 'Veg and non-veg',
};

export const ONBOARDING_SOURCES = ['ops', 'mi', 'self'] as const;
export const OnboardingSourceSchema = z.enum(ONBOARDING_SOURCES);
export type OnboardingSource = z.infer<typeof OnboardingSourceSchema>;

/**
 * Outlet onboarding states:
 *   draft → submitted → in_review → approved → live ⇄ paused
 *                          ↘ changes_requested → submitted …
 *   any state before live → rejected (reviewers can reopen to draft)
 */
export const OUTLET_STATUSES = [
  'draft',
  'submitted',
  'in_review',
  'changes_requested',
  'approved',
  'live',
  'paused',
  'rejected',
] as const;
export const OutletStatusSchema = z.enum(OUTLET_STATUSES);
export type OutletStatus = z.infer<typeof OutletStatusSchema>;

export const OUTLET_STATUS_LABELS: Record<OutletStatus, string> = {
  draft: 'Draft',
  submitted: 'Waiting for review',
  in_review: 'In review',
  changes_requested: 'Changes asked',
  approved: 'Approved',
  live: 'Live',
  paused: 'Paused',
  rejected: 'Rejected',
};

/** Statuses in which the restaurant side (MI, owner, Ops) may still edit details and documents. */
export const EDITABLE_OUTLET_STATUSES: readonly OutletStatus[] = ['draft', 'changes_requested'];

/* ------------------------------------------------------------------ */
/* Document catalogue                                                  */
/* ------------------------------------------------------------------ */

export const DOC_TYPES = [
  'fssai_licence',
  'pan_card',
  'gst_certificate',
  'dftqc_licence',
  'ward_permit',
  'pan_vat_certificate',
  'owner_id',
  'business_proof',
  'cancelled_cheque',
  'kitchen_photo',
  'facade_photo',
  'menu_photo',
] as const;
export const DocTypeSchema = z.enum(DOC_TYPES);
export type DocType = z.infer<typeof DocTypeSchema>;

export type DocLevel = 'restaurant' | 'outlet';
export type DocKind = 'licence' | 'tax' | 'identity' | 'business' | 'bank' | 'photo';

export interface DocTypeDef {
  type: DocType;
  label: string;
  hint: string;
  level: DocLevel;
  kind: DocKind;
  countries: readonly CountryCode[];
  /** 'tax_registered' = required only when the restaurant says it is GST / VAT registered. */
  required: boolean | 'tax_registered';
  /** At most this many active files of this type (photos allow several). */
  maxFiles: number;
  number: null | {
    label: string;
    pattern: RegExp;
    example: string;
    /** Sensitive numbers are stored encrypted and only the last 4 are ever shown. */
    sensitive: boolean;
    required: boolean;
  };
  expiry: 'required' | 'optional' | 'none';
  /** Photos must carry where and when they were taken. */
  geotagged: boolean;
  /** Bank details (account holder, account number, IFSC / bank) come with this file. */
  bank: boolean;
  /** The owner's ID type must be given (the ID number itself is never stored). */
  idType: boolean;
}

export const ID_TYPES = [
  'aadhaar',
  'voter_id',
  'passport',
  'driving_licence',
  'citizenship',
  'national_id',
] as const;
export const IdTypeSchema = z.enum(ID_TYPES);
export type IdType = z.infer<typeof IdTypeSchema>;
export const ID_TYPE_LABELS: Record<IdType, string> = {
  aadhaar: 'Aadhaar (masked copy)',
  voter_id: 'Voter ID',
  passport: 'Passport',
  driving_licence: 'Driving licence',
  citizenship: 'Citizenship certificate',
  national_id: 'National ID card',
};
export const ID_TYPES_BY_COUNTRY: Record<CountryCode, readonly IdType[]> = {
  IN: ['aadhaar', 'voter_id', 'passport', 'driving_licence'],
  NP: ['citizenship', 'national_id', 'passport', 'driving_licence'],
};

const IMAGE_OR_PDF = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
const IMAGE_ONLY = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ACCEPTED_CONTENT_TYPES = IMAGE_OR_PDF;
export type AcceptedContentType = (typeof IMAGE_OR_PDF)[number];

/** Largest accepted file. Phones produce 2–6 MB photos; scans are smaller. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

export const DOC_CATALOGUE: readonly DocTypeDef[] = [
  {
    type: 'fssai_licence',
    label: 'FSSAI licence',
    hint: 'Licence or registration for this kitchen. The number is shown to customers.',
    level: 'outlet',
    kind: 'licence',
    countries: ['IN'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'FSSAI number',
      pattern: /^\d{14}$/,
      example: '12345678901234',
      sensitive: false,
      required: true,
    },
    expiry: 'required',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'pan_card',
    label: 'PAN card',
    hint: 'PAN of the business (or the owner for a sole proprietor).',
    level: 'restaurant',
    kind: 'tax',
    countries: ['IN'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'PAN',
      pattern: /^[A-Z]{5}\d{4}[A-Z]$/,
      example: 'ABCDE1234F',
      sensitive: true,
      required: true,
    },
    expiry: 'none',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'gst_certificate',
    label: 'GST certificate',
    hint: 'Only if the business is GST registered.',
    level: 'restaurant',
    kind: 'tax',
    countries: ['IN'],
    required: 'tax_registered',
    maxFiles: 1,
    number: {
      label: 'GSTIN',
      pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
      example: '09ABCDE1234F1Z5',
      sensitive: false,
      required: true,
    },
    expiry: 'none',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'dftqc_licence',
    label: 'DFTQC licence',
    hint: 'Food licence from the Department of Food Technology and Quality Control.',
    level: 'outlet',
    kind: 'licence',
    countries: ['NP'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'Licence number',
      pattern: /^[A-Za-z0-9/-]{3,30}$/,
      example: '079/080-1234',
      sensitive: false,
      required: true,
    },
    expiry: 'required',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'ward_permit',
    label: 'Ward permit',
    hint: 'Business registration with the ward office for this kitchen.',
    level: 'outlet',
    kind: 'licence',
    countries: ['NP'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'Permit number',
      pattern: /^[A-Za-z0-9/-]{1,30}$/,
      example: '1234',
      sensitive: false,
      required: false,
    },
    expiry: 'optional',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'pan_vat_certificate',
    label: 'PAN / VAT certificate',
    hint: 'Inland Revenue PAN certificate; VAT certificate if VAT registered.',
    level: 'restaurant',
    kind: 'tax',
    countries: ['NP'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'PAN number',
      pattern: /^\d{9}$/,
      example: '123456789',
      sensitive: false,
      required: true,
    },
    expiry: 'none',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'owner_id',
    label: 'Owner ID',
    hint: 'Government photo ID of the owner. For Aadhaar, upload the masked copy.',
    level: 'restaurant',
    kind: 'identity',
    countries: ['IN', 'NP'],
    required: true,
    maxFiles: 2,
    number: null,
    expiry: 'none',
    geotagged: false,
    bank: false,
    idType: true,
  },
  {
    type: 'business_proof',
    label: 'Business proof',
    hint: 'Shop & establishment, trade licence, Udyam or company / firm registration.',
    level: 'restaurant',
    kind: 'business',
    countries: ['IN', 'NP'],
    required: true,
    maxFiles: 1,
    number: {
      label: 'Registration number',
      pattern: /^[A-Za-z0-9/-]{3,40}$/,
      example: 'UDYAM-UP-12-0012345',
      sensitive: false,
      required: false,
    },
    expiry: 'optional',
    geotagged: false,
    bank: false,
    idType: false,
  },
  {
    type: 'cancelled_cheque',
    label: 'Cancelled cheque',
    hint: 'Bank account for payouts. The account name must match the business or owner.',
    level: 'restaurant',
    kind: 'bank',
    countries: ['IN', 'NP'],
    required: true,
    maxFiles: 1,
    number: null,
    expiry: 'none',
    geotagged: false,
    bank: true,
    idType: false,
  },
  {
    type: 'kitchen_photo',
    label: 'Kitchen photo',
    hint: 'Taken in the kitchen with location on. The number needed is set in Settings.',
    level: 'outlet',
    kind: 'photo',
    countries: ['IN', 'NP'],
    required: true,
    maxFiles: 6,
    number: null,
    expiry: 'none',
    geotagged: true,
    bank: false,
    idType: false,
  },
  {
    type: 'facade_photo',
    label: 'Shop front photo',
    hint: 'Optional. Helps riders find the place.',
    level: 'outlet',
    kind: 'photo',
    countries: ['IN', 'NP'],
    required: false,
    maxFiles: 2,
    number: null,
    expiry: 'none',
    geotagged: true,
    bank: false,
    idType: false,
  },
  {
    type: 'menu_photo',
    label: 'Menu card photo',
    hint: 'Optional. Used to build the menu and to check app prices.',
    level: 'outlet',
    kind: 'photo',
    countries: ['IN', 'NP'],
    required: false,
    maxFiles: 10,
    number: null,
    expiry: 'none',
    geotagged: false,
    bank: false,
    idType: false,
  },
];

const DOC_BY_TYPE = new Map(DOC_CATALOGUE.map((def) => [def.type, def]));

export function docDef(type: DocType): DocTypeDef {
  const def = DOC_BY_TYPE.get(type);
  if (!def) throw new Error(`Unknown document type ${type}`);
  return def;
}

export function docTypesFor(country: CountryCode): DocTypeDef[] {
  return DOC_CATALOGUE.filter((def) => def.countries.includes(country));
}

export function isDocRequired(def: DocTypeDef, taxRegistered: boolean): boolean {
  return def.required === 'tax_registered' ? taxRegistered : def.required;
}

export function acceptedTypesFor(def: DocTypeDef): readonly string[] {
  return def.kind === 'photo' ? IMAGE_ONLY : IMAGE_OR_PDF;
}

/* ------------------------------------------------------------------ */
/* Document states, checks and views                                   */
/* ------------------------------------------------------------------ */

/**
 * pending_upload  the upload link was handed out, the file has not been confirmed yet
 * uploaded        file confirmed and automatic checks run; waiting for a reviewer
 * verified        a reviewer accepted it
 * rejected        a reviewer refused it (reason kept); a new file is needed
 * expired         its expiry date passed (set by the daily sweep)
 * replaced        a newer file of the same type took its place
 * removed         taken out before review
 */
export const DOC_STATUSES = [
  'pending_upload',
  'uploaded',
  'verified',
  'rejected',
  'expired',
  'replaced',
  'removed',
] as const;
export const DocStatusSchema = z.enum(DOC_STATUSES);
export type DocStatus = z.infer<typeof DocStatusSchema>;

/** Documents that still count towards the checklist. */
export const ACTIVE_DOC_STATUSES: readonly DocStatus[] = ['uploaded', 'verified'];

export const DocCheckSchema = z.object({
  code: z.string(),
  result: z.enum(['pass', 'warn', 'fail']),
  message: z.string(),
});
export type DocCheck = z.infer<typeof DocCheckSchema>;

export const VERIFICATION_RESULTS = ['verified', 'mismatch', 'not_found', 'unavailable'] as const;
export const VerificationSchema = z.object({
  provider: z.string(),
  result: z.enum(VERIFICATION_RESULTS),
  detail: z.string().nullable(),
  checkedAt: z.string(),
});
export type Verification = z.infer<typeof VerificationSchema>;

export const KycDocumentSchema = z.object({
  id: z.uuid(),
  restaurantId: z.uuid(),
  outletId: z.uuid().nullable(),
  docType: DocTypeSchema,
  status: DocStatusSchema,
  fileName: z.string().nullable(),
  contentType: z.string(),
  sizeBytes: z.number().int().nullable(),
  /** Full number when it is not sensitive (licences, GSTIN); otherwise "•••• 1234". */
  number: z.string().nullable(),
  expiresOn: z.string().nullable(),
  idType: IdTypeSchema.nullable(),
  bank: z
    .object({
      accountHolder: z.string(),
      accountLast4: z.string(),
      ifsc: z.string().nullable(),
      bankName: z.string().nullable(),
    })
    .nullable(),
  photo: z
    .object({
      lat: z.number(),
      lng: z.number(),
      capturedAt: z.string(),
      distanceM: z.number().nullable(),
    })
    .nullable(),
  checks: z.array(DocCheckSchema),
  verification: VerificationSchema.nullable(),
  reviewNote: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  uploadedBy: z.string(),
  uploadedAt: z.string(),
  version: z.number().int(),
});
export type KycDocument = z.infer<typeof KycDocumentSchema>;

/* ------------------------------------------------------------------ */
/* Inputs                                                               */
/* ------------------------------------------------------------------ */

const DateSchema = z.iso.date('Use a date like 2027-03-31');

export const GeoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type GeoPoint = z.infer<typeof GeoPointSchema>;

const CuisineSchema = z.string().trim().min(2).max(40);

export const OutletFieldsSchema = z.object({
  name: z.string().trim().min(2).max(80),
  townId: z.uuid(),
  address: z.string().trim().min(5).max(300),
  landmark: z.string().trim().min(2).max(120).nullable().optional(),
  location: GeoPointSchema,
  phone: PhoneSchema.nullable().optional(),
  cuisines: z.array(CuisineSchema).min(1, 'Add at least one cuisine').max(8),
  foodType: FoodTypeSchema,
});
export type OutletFields = z.infer<typeof OutletFieldsSchema>;

export const RestaurantFieldsSchema = z.object({
  legalName: z.string().trim().min(2).max(120),
  displayName: z.string().trim().min(2).max(80),
  businessType: BusinessTypeSchema,
  /** GST registered (India) or VAT registered (Nepal). */
  taxRegistered: z.boolean(),
});
export type RestaurantFields = z.infer<typeof RestaurantFieldsSchema>;

export const OwnerFieldsSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: PhoneSchema,
});
export type OwnerFields = z.infer<typeof OwnerFieldsSchema>;

export const CreateRestaurantInputSchema = z.object({
  restaurant: RestaurantFieldsSchema,
  owner: OwnerFieldsSchema,
  outlet: OutletFieldsSchema,
});
export type CreateRestaurantInput = z.infer<typeof CreateRestaurantInputSchema>;

export const AddOutletInputSchema = z.object({ outlet: OutletFieldsSchema });
export type AddOutletInput = z.infer<typeof AddOutletInputSchema>;

export const UpdateOutletInputSchema = OutletFieldsSchema.partial()
  .extend({ expectedVersion: z.number().int().min(1) })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateOutletInput = z.infer<typeof UpdateOutletInputSchema>;

export const UpdateRestaurantInputSchema = RestaurantFieldsSchema.partial()
  .extend({ expectedVersion: z.number().int().min(1) })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateRestaurantInput = z.infer<typeof UpdateRestaurantInputSchema>;

export const StartUploadInputSchema = z.object({
  docType: DocTypeSchema,
  fileName: z.string().trim().min(1).max(120),
  contentType: z.enum(IMAGE_OR_PDF),
  sizeBytes: z.number().int().min(1).max(MAX_DOCUMENT_BYTES, 'Files can be at most 10 MB'),
});
export type StartUploadInput = z.infer<typeof StartUploadInputSchema>;

export const UploadTicketSchema = z.object({
  documentId: z.uuid(),
  /** PUT the file here with exactly these headers, before expiresAt. */
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type UploadTicket = z.infer<typeof UploadTicketSchema>;

export const BankDetailsInputSchema = z.object({
  accountHolder: z.string().trim().min(2).max(120),
  accountNumber: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{6,24}$/, 'Account numbers are 6–24 letters or digits'),
  ifsc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC looks like HDFC0001234')
    .nullable()
    .optional(),
  bankName: z.string().trim().min(2).max(80).nullable().optional(),
});
export type BankDetailsInput = z.infer<typeof BankDetailsInputSchema>;

export const PhotoMetaInputSchema = GeoPointSchema.extend({
  capturedAt: z.iso.datetime({ offset: true }),
});
export type PhotoMetaInput = z.infer<typeof PhotoMetaInputSchema>;

export const CompleteUploadInputSchema = z.object({
  number: z.string().trim().toUpperCase().min(1).max(40).nullable().optional(),
  expiresOn: DateSchema.nullable().optional(),
  idType: IdTypeSchema.nullable().optional(),
  bank: BankDetailsInputSchema.nullable().optional(),
  photo: PhotoMetaInputSchema.nullable().optional(),
});
export type CompleteUploadInput = z.infer<typeof CompleteUploadInputSchema>;

const ReasonSchema = z.string().trim().min(3, 'Say why (at least 3 characters)').max(300);

export const ReviewDocumentInputSchema = z.discriminatedUnion('decision', [
  z.object({
    decision: z.literal('verify'),
    expectedVersion: z.number().int().min(1),
    note: z.string().trim().max(300).nullable().optional(),
  }),
  z.object({
    decision: z.literal('reject'),
    expectedVersion: z.number().int().min(1),
    note: ReasonSchema,
  }),
]);
export type ReviewDocumentInput = z.infer<typeof ReviewDocumentInputSchema>;

export const OUTLET_ACTIONS = [
  'submit',
  'start_review',
  'request_changes',
  'approve',
  'reject',
  'go_live',
  'pause',
  'resume',
  'reopen',
  'mark_trained',
] as const;
export const OutletActionSchema = z.enum(OUTLET_ACTIONS);
export type OutletAction = z.infer<typeof OutletActionSchema>;

/** Actions that must carry a reason. */
export const ACTIONS_NEEDING_REASON: readonly OutletAction[] = [
  'request_changes',
  'reject',
  'pause',
  'reopen',
];

export const OutletActionInputSchema = z
  .object({
    action: OutletActionSchema,
    expectedVersion: z.number().int().min(1),
    reason: z.string().trim().max(300).nullable().optional(),
  })
  .refine(
    (v) => !ACTIONS_NEEDING_REASON.includes(v.action) || (v.reason ?? '').trim().length >= 3,
    { message: 'Say why (at least 3 characters)', path: ['reason'] },
  );
export type OutletActionInput = z.infer<typeof OutletActionInputSchema>;

/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */

export const ChecklistItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  done: z.boolean(),
  /** One line on what is missing; null when done. */
  detail: z.string().nullable(),
  /** Blocks submitting, approving or going live. */
  blocks: z.enum(['submit', 'approve', 'go_live', 'none']),
});
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

export const ChecklistSchema = z.object({
  items: z.array(ChecklistItemSchema),
  canSubmit: z.boolean(),
  canApprove: z.boolean(),
  canGoLive: z.boolean(),
  docsDone: z.number().int(),
  docsRequired: z.number().int(),
});
export type Checklist = z.infer<typeof ChecklistSchema>;

export const OutletRowSchema = z.object({
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantId: z.uuid(),
  restaurantName: z.string(),
  country: CountryCodeSchema,
  townId: z.uuid(),
  townName: z.string(),
  zoneCode: z.string().nullable(),
  status: OutletStatusSchema,
  source: OnboardingSourceSchema,
  docsDone: z.number().int(),
  docsRequired: z.number().int(),
  failingChecks: z.number().int(),
  submittedAt: z.string().nullable(),
  reviewDueAt: z.string().nullable(),
  reviewer: z.string().nullable(),
  reviewerName: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  updatedAt: z.string(),
});
export type OutletRow = z.infer<typeof OutletRowSchema>;

export const OUTLET_VIEWS = [
  'queue',
  'all',
  'drafts',
  'changes_requested',
  'approved',
  'live',
  'paused',
  'rejected',
  'expiring',
] as const;
export const OutletViewSchema = z.enum(OUTLET_VIEWS);
export type OutletView = z.infer<typeof OutletViewSchema>;

export const OutletListQuerySchema = z.object({
  view: OutletViewSchema.default('queue'),
  country: CountryCodeSchema.optional(),
  townId: z.uuid().optional(),
  q: z.string().trim().min(2).max(80).optional(),
});
export type OutletListQuery = z.infer<typeof OutletListQuerySchema>;

export const OutletCountsSchema = z.record(OutletViewSchema, z.number().int());
export type OutletCounts = z.infer<typeof OutletCountsSchema>;

export const OutletListSchema = z.object({
  rows: z.array(OutletRowSchema),
  counts: OutletCountsSchema,
});
export type OutletList = z.infer<typeof OutletListSchema>;

export const OnboardingEventSchema = z.object({
  id: z.number().int(),
  outletId: z.uuid().nullable(),
  action: z.string(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  reason: z.string().nullable(),
  actor: z.string(),
  actorName: z.string().nullable(),
  summary: z.string(),
  at: z.string(),
});
export type OnboardingEvent = z.infer<typeof OnboardingEventSchema>;

export const AGREEMENT_STATES = ['signed', 'outdated', 'not_signed', 'no_template'] as const;
export const AgreementStateSchema = z.enum(AGREEMENT_STATES);
export type AgreementState = z.infer<typeof AgreementStateSchema>;

export const AgreementStatusSchema = z.object({
  state: AgreementStateSchema,
  /** Latest published version for the country, if any. */
  currentVersion: z.number().int().nullable(),
  signedVersion: z.number().int().nullable(),
  signedAt: z.string().nullable(),
  signedBy: z.string().nullable(),
  channel: z.enum(['assisted', 'self']).nullable(),
});
export type AgreementStatus = z.infer<typeof AgreementStatusSchema>;

export const OutletDetailSchema = z.object({
  outlet: z.object({
    id: z.uuid(),
    name: z.string(),
    status: OutletStatusSchema,
    source: OnboardingSourceSchema,
    townId: z.uuid(),
    townName: z.string(),
    townStatus: z.string(),
    zoneId: z.uuid().nullable(),
    zoneCode: z.string().nullable(),
    zoneStatus: z.string().nullable(),
    address: z.string(),
    landmark: z.string().nullable(),
    location: GeoPointSchema,
    phoneMasked: z.string().nullable(),
    cuisines: z.array(z.string()),
    foodType: FoodTypeSchema,
    submittedAt: z.string().nullable(),
    reviewDueAt: z.string().nullable(),
    reviewer: z.string().nullable(),
    reviewerName: z.string().nullable(),
    approvedAt: z.string().nullable(),
    liveAt: z.string().nullable(),
    trainedAt: z.string().nullable(),
    createdBy: z.string(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    version: z.number().int(),
  }),
  restaurant: z.object({
    id: z.uuid(),
    country: CountryCodeSchema,
    legalName: z.string(),
    displayName: z.string(),
    businessType: BusinessTypeSchema,
    taxRegistered: z.boolean(),
    version: z.number().int(),
    outlets: z.array(z.object({ id: z.uuid(), name: z.string(), status: OutletStatusSchema })),
  }),
  owner: z.object({
    identityId: z.uuid(),
    name: z.string().nullable(),
    phoneMasked: z.string(),
    status: z.string(),
  }),
  documents: z.array(KycDocumentSchema),
  checklist: ChecklistSchema,
  agreement: AgreementStatusSchema,
  events: z.array(OnboardingEventSchema),
});
export type OutletDetail = z.infer<typeof OutletDetailSchema>;

export const CreatedRestaurantSchema = z.object({
  restaurantId: z.uuid(),
  outletId: z.uuid(),
});
export type CreatedRestaurant = z.infer<typeof CreatedRestaurantSchema>;

export const DocumentLinkSchema = z.object({ url: z.string(), expiresAt: z.string() });
export type DocumentLink = z.infer<typeof DocumentLinkSchema>;

/* ------------------------------------------------------------------ */
/* Agreements                                                          */
/* ------------------------------------------------------------------ */

export const AGREEMENT_TEMPLATE_STATUSES = ['draft', 'published', 'retired'] as const;
export const AgreementTemplateStatusSchema = z.enum(AGREEMENT_TEMPLATE_STATUSES);
export type AgreementTemplateStatus = z.infer<typeof AgreementTemplateStatusSchema>;

/**
 * Placeholders a template body may use. {{config:<key>}} inserts the setting in force for the
 * restaurant's town (formatted with its unit), so no fee is ever typed into the text.
 */
export const AGREEMENT_FIELDS = [
  'restaurant.legal_name',
  'restaurant.display_name',
  'restaurant.business_type',
  'owner.name',
  'owner.phone_masked',
  'outlets',
  'country',
  'date',
  'version',
] as const;
export type AgreementField = (typeof AGREEMENT_FIELDS)[number];

export const AgreementTemplateSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema,
  version: z.number().int(),
  title: z.string(),
  body: z.string(),
  status: AgreementTemplateStatusSchema,
  publishReason: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  revision: z.number().int(),
  signedCount: z.number().int(),
});
export type AgreementTemplate = z.infer<typeof AgreementTemplateSchema>;

export const AGREEMENT_BODY_MAX = 40_000;

export const SaveAgreementDraftInputSchema = z.object({
  country: CountryCodeSchema,
  title: z.string().trim().min(3).max(120),
  body: z.string().trim().min(20).max(AGREEMENT_BODY_MAX),
  /** null when starting a new draft; the draft's revision when changing it. */
  expectedRevision: z.number().int().min(1).nullable(),
});
export type SaveAgreementDraftInput = z.infer<typeof SaveAgreementDraftInputSchema>;

export const PublishAgreementInputSchema = z.object({
  expectedRevision: z.number().int().min(1),
  reason: ReasonSchema,
});
export type PublishAgreementInput = z.infer<typeof PublishAgreementInputSchema>;

export const AgreementPreviewInputSchema = z.object({
  title: z.string().trim().min(3).max(120),
  body: z.string().trim().min(1).max(AGREEMENT_BODY_MAX),
  country: CountryCodeSchema,
  /** Render for this outlet's restaurant and town; sample values otherwise. */
  outletId: z.uuid().nullable().optional(),
});
export type AgreementPreviewInput = z.infer<typeof AgreementPreviewInputSchema>;

export const RenderedAgreementSchema = z.object({
  templateId: z.uuid().nullable(),
  version: z.number().int().nullable(),
  title: z.string(),
  body: z.string(),
  /** SHA-256 of title + body as shown. The signature binds to exactly this text. */
  bodySha256: z.string(),
  /** Placeholders that could not be filled (shown as [missing: …] in the text). */
  missing: z.array(z.string()),
});
export type RenderedAgreement = z.infer<typeof RenderedAgreementSchema>;

export const SignAgreementInputSchema = z.object({
  templateId: z.uuid(),
  bodySha256: z.string().regex(/^[0-9a-f]{64}$/),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export type SignAgreementInput = z.infer<typeof SignAgreementInputSchema>;

export const AgreementCodeSentSchema = z.object({
  sentTo: z.string(),
  resendInSeconds: z.number().int(),
  expiresInSeconds: z.number().int(),
  smsDelayed: z.boolean(),
});
export type AgreementCodeSent = z.infer<typeof AgreementCodeSentSchema>;
